/**
 * PRD.291 — Today's Prescriptions (Pharmacy Billing Queue).
 *
 * Read-only queries that surface prescriptions written for today's visits so a
 * pharmacist can convert all or part of a prescription into an invoice before
 * payment + dispensing. All writes reuse the existing Billing flow — this
 * module only reads.
 *
 * "Invoiced" is tracked precisely via the invoice line's provenance:
 * `invoice_items.source_type = 'prescription' AND source_id = <prescription id>`
 * (set by the existing "Add Items from Prescription" flow, PRD.210). A
 * prescription drops off the queue once every INVENTORY-BOUND line has been
 * invoiced; partially-invoiced prescriptions stay until fully billed.
 */
import type { QuerySpec } from '@helios/powersync-client-core';
import type { InvoicePrescriptionCandidateRow } from './patient-consultation';

// ─── Today's Prescriptions — one row per prescription (encounter) ───

export interface TodaysPrescriptionRow {
  encounter_id: string;
  patient_id: string | null;
  patient_name: string | null;
  /** Prescription/visit timestamp (encounter started_at, else earliest line). */
  prescription_date: string | null;
  provider_id: string | null;
  provider_name: string | null;
  /** Count of prescribed medicine lines (all final lines). */
  total_meds: number;
  /** Inventory-bound lines (the only ones that can be invoiced). */
  billable_meds: number;
  /** Inventory-bound lines placed on ANY invoice (draft or paid). */
  invoiced_meds: number;
  /** Inventory-bound lines placed on a PAID invoice. */
  paid_meds: number;
  /**
   * An invoice id linked to this prescription that is NOT yet fully paid — the
   * "Collect Payment" target once every billable line has been invoiced but not
   * all are paid. Null when nothing is pending payment.
   */
  pending_invoice_id: string | null;
}

/**
 * Prescriptions for TODAY's visits that still have at least one inventory-bound
 * medicine not yet invoiced. `todayIso` is the local YYYY-MM-DD (SQLite's
 * date('now') is UTC, so the caller passes local "today" to avoid day-boundary
 * drift — mirrors the existing invoice date-scoping).
 *
 * Grouped one row per encounter (= one prescription). A prescription stays in
 * the queue until every billable line is on a PAID invoice (HAVING
 * billable_meds > paid_meds) — so a billed-but-unpaid prescription does NOT
 * silently disappear; the pharmacist finishes payment from the same queue.
 * "Paid" mirrors Ready-to-Dispense eligibility:
 * invoice.status='paid' OR (invoice.status='issued' AND item.payment_status='paid').
 */
export function qTodaysPrescriptions(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-todays-prescriptions', todayIso] as const,
    sql: `
      SELECT
        e.id                                   AS encounter_id,
        p.patient_id                           AS patient_id,
        pt.full_name                           AS patient_name,
        COALESCE(e.started_at, MIN(p.created_at)) AS prescription_date,
        e.provider_id                          AS provider_id,
        COALESCE(prov.display_name, prov.full_name) AS provider_name,
        COUNT(*)                               AS total_meds,
        SUM(CASE WHEN p.inventory_item_id IS NOT NULL THEN 1 ELSE 0 END) AS billable_meds,
        SUM(CASE WHEN p.inventory_item_id IS NOT NULL AND EXISTS(
              SELECT 1 FROM invoice_items ii
              WHERE ii.source_type = 'prescription' AND ii.source_id = p.id
            ) THEN 1 ELSE 0 END)               AS invoiced_meds,
        SUM(CASE WHEN p.inventory_item_id IS NOT NULL AND EXISTS(
              SELECT 1 FROM invoice_items ii
              JOIN invoices i2 ON i2.id = ii.invoice_id
              WHERE ii.source_type = 'prescription' AND ii.source_id = p.id
                AND (i2.status = 'paid' OR (i2.status = 'issued' AND ii.payment_status = 'paid'))
            ) THEN 1 ELSE 0 END)               AS paid_meds,
        (
          SELECT ii.invoice_id
          FROM invoice_items ii
          JOIN patient_prescriptions p2 ON p2.id = ii.source_id
          JOIN invoices i2 ON i2.id = ii.invoice_id
          WHERE ii.source_type = 'prescription'
            AND p2.encounter_id = e.id
            AND NOT (i2.status = 'paid' OR (i2.status = 'issued' AND ii.payment_status = 'paid'))
          ORDER BY i2.created_at DESC
          LIMIT 1
        )                                      AS pending_invoice_id
      FROM patient_prescriptions p
      JOIN patient_encounters e ON e.id = p.encounter_id
      LEFT JOIN appointment_providers prov ON prov.id = e.provider_id
      LEFT JOIN patients pt ON pt.id = p.patient_id
      WHERE (p.status IS NULL OR p.status = 'final')
        AND date(COALESCE(e.started_at, p.created_at)) = date(?)
      GROUP BY e.id, p.patient_id
      HAVING SUM(CASE WHEN p.inventory_item_id IS NOT NULL THEN 1 ELSE 0 END)
           > SUM(CASE WHEN p.inventory_item_id IS NOT NULL AND EXISTS(
                 SELECT 1 FROM invoice_items ii
                 JOIN invoices i2 ON i2.id = ii.invoice_id
                 WHERE ii.source_type = 'prescription' AND ii.source_id = p.id
                   AND (i2.status = 'paid' OR (i2.status = 'issued' AND ii.payment_status = 'paid'))
               ) THEN 1 ELSE 0 END)
      ORDER BY prescription_date DESC
    `,
    params: [todayIso],
    watch: [
      'patient_prescriptions',
      'patient_encounters',
      'appointment_providers',
      'patients',
      'invoice_items',
      'invoices',
    ],
  };
}

/**
 * PRD.293 — ALL of a patient's prescriptions (one row per encounter, any date),
 * including fully-invoiced/served ones. Same shape + counts as
 * `qTodaysPrescriptions` but scoped to one patient and WITHOUT the date filter
 * or the "still has an uninvoiced line" HAVING filter — so the New Pharmacy Sale
 * drawer can section them (Today / earlier-unbilled / already-served) instead of
 * hiding served prescriptions (and reporting a misleading "no prescriptions").
 * Newest first, capped so a long history stays bounded.
 */
export function qPatientPrescriptions(patientId: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-patient-prescriptions', patientId] as const,
    sql: `
      SELECT
        e.id                                   AS encounter_id,
        p.patient_id                           AS patient_id,
        pt.full_name                           AS patient_name,
        COALESCE(e.started_at, MIN(p.created_at)) AS prescription_date,
        e.provider_id                          AS provider_id,
        COALESCE(prov.display_name, prov.full_name) AS provider_name,
        COUNT(*)                               AS total_meds,
        SUM(CASE WHEN p.inventory_item_id IS NOT NULL THEN 1 ELSE 0 END) AS billable_meds,
        SUM(CASE WHEN p.inventory_item_id IS NOT NULL AND EXISTS(
              SELECT 1 FROM invoice_items ii
              WHERE ii.source_type = 'prescription' AND ii.source_id = p.id
            ) THEN 1 ELSE 0 END)               AS invoiced_meds,
        SUM(CASE WHEN p.inventory_item_id IS NOT NULL AND EXISTS(
              SELECT 1 FROM invoice_items ii
              JOIN invoices i2 ON i2.id = ii.invoice_id
              WHERE ii.source_type = 'prescription' AND ii.source_id = p.id
                AND (i2.status = 'paid' OR (i2.status = 'issued' AND ii.payment_status = 'paid'))
            ) THEN 1 ELSE 0 END)               AS paid_meds,
        (
          SELECT ii.invoice_id
          FROM invoice_items ii
          JOIN patient_prescriptions p2 ON p2.id = ii.source_id
          JOIN invoices i2 ON i2.id = ii.invoice_id
          WHERE ii.source_type = 'prescription'
            AND p2.encounter_id = e.id
            AND NOT (i2.status = 'paid' OR (i2.status = 'issued' AND ii.payment_status = 'paid'))
          ORDER BY i2.created_at DESC
          LIMIT 1
        )                                      AS pending_invoice_id
      FROM patient_prescriptions p
      JOIN patient_encounters e ON e.id = p.encounter_id
      LEFT JOIN appointment_providers prov ON prov.id = e.provider_id
      LEFT JOIN patients pt ON pt.id = p.patient_id
      WHERE (p.status IS NULL OR p.status = 'final')
        AND p.patient_id = ?
      GROUP BY e.id, p.patient_id
      ORDER BY prescription_date DESC
      LIMIT 200
    `,
    params: [patientId],
    watch: [
      'patient_prescriptions',
      'patient_encounters',
      'appointment_providers',
      'patients',
      'invoice_items',
      'invoices',
    ],
  };
}

// ─── Inventory-bound prescription lines for an encounter (selection + add) ───

/**
 * Candidate-shaped row (compatible with the existing "Add Items from
 * Prescription" flow) plus an `already_invoiced` flag. Used by the pharmacy
 * medicine-selection step and, filtered by id, by the Billing auto-add.
 *
 * Unlike `qInvoicePrescriptionCandidates`, this does NOT exclude closed
 * encounters — a pharmacist bills after the consultation has ended.
 */
export interface TodaysPrescriptionLineRow extends InvoicePrescriptionCandidateRow {
  already_invoiced: number; // 0 | 1
}

export function qPrescriptionInvoiceLinesByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['pharmacy-rx-invoice-lines', encounterId] as const,
    sql: `
      SELECT
        p.id                              AS prescription_id,
        p.patient_id                      AS patient_id,
        p.encounter_id                    AS encounter_id,
        p.inventory_item_id               AS inventory_item_id,
        p.item_name                       AS item_name,
        p.item_details                    AS item_details,
        p.free_text_name                  AS free_text_name,
        p.item_name_snapshot              AS item_name_snapshot,
        p.sku_snapshot                    AS sku_snapshot,
        p.brand_name_snapshot             AS brand_name_snapshot,
        p.generic_name_snapshot           AS generic_name_snapshot,
        p.strength_snapshot               AS strength_snapshot,
        p.dosage_form_snapshot            AS dosage_form_snapshot,
        p.route_snapshot                  AS route_snapshot,
        p.unit_snapshot                   AS unit_snapshot,
        p.default_instruction_snapshot    AS default_instruction_snapshot,
        p.default_dosage_snapshot         AS default_dosage_snapshot,
        p.usage_snapshot                  AS usage_snapshot,
        p.dose_snapshot                   AS dose_snapshot,
        p.dose_unit_snapshot              AS dose_unit_snapshot,
        p.frequency_snapshot              AS frequency_snapshot,
        p.duration_snapshot               AS duration_snapshot,
        inv.item_type                     AS inventory_item_type,
        inv.selling_price                 AS inventory_selling_price,
        inv.inventory_unit                AS inventory_unit,
        inv.status                        AS inventory_status,
        (CASE WHEN EXISTS(
           SELECT 1 FROM invoice_items ii
           WHERE ii.source_type = 'prescription' AND ii.source_id = p.id
         ) THEN 1 ELSE 0 END)             AS already_invoiced
      FROM patient_prescriptions p
      LEFT JOIN inventory_items inv ON inv.id = p.inventory_item_id
      WHERE p.encounter_id = ?
        AND p.inventory_item_id IS NOT NULL
        AND (p.status IS NULL OR p.status = 'final')
      ORDER BY p.sort_order ASC, p.created_at ASC
    `,
    params: [encounterId],
    watch: ['patient_prescriptions', 'inventory_items', 'invoice_items'],
  };
}
